import LockClosedIcon from '@heroicons/react/outline/LockClosedIcon'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import type {
  Post,
  PostListFilterByInput,
} from '@tribeplatform/gql-client/types'
import {
  PostListFilterByEnum,
  PostListFilterByOperator,
} from '@tribeplatform/gql-client/types'
import { T, useStaticIntl } from '@tribeplatform/react-components/i18n'
import { usePosts, useSpace } from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { EmptyState } from '@tribeplatform/react-ui-kit/EmptyState'

import { useQuery } from '../../../lib/useQuery'
import { convertToStatic } from '../../Views/utils'
import { staticFieldsFactory } from '../config'
import { useCustomFields } from '../hooks/useCustomFields'
import { useShowMoreButton } from '../hooks/useShowMoreButton'
import type { PostsBlockView } from '../types'
import { getFilterKeyString, getFilterOperator } from '../utils'
import { generateGqlFields } from '../utils/generateGqlFields'
import { EmptyState as PostsEmptyState } from './EmptyState'
import { GenericPostsList } from './GenericPostsList'
import { LoadingState } from './LoadingState'

interface Props {
  view: PostsBlockView
  activeFieldId: string
  preview: boolean
}
export const SpacePostsList = ({
  view: {
    spaceId,
    sort: orderBy,
    reverseSort,
    filterBy,
    limit,
    style,
    fields,
    postTypeId: id,
    postTypeSlug,
    gallerySize,
    showDivider,
    showMore,
    linkTo,
  },
  activeFieldId,
  preview,
}: Props) => {
  const staticFields = useStaticIntl(staticFieldsFactory)
  const { data: space } = useSpace({
    variables: { id: spaceId },
  })

  const { tag_id: tagId } = useQuery()

  const { customFields, postTypeId } = useCustomFields({
    id,
    slug: postTypeSlug,
  })
  const [canGetPosts] = hasScopesPermission(space, ['posts'])

  const postsFilterBy =
    filterBy.map<PostListFilterByInput>(({ key, operator, value }) => {
      return {
        key: PostListFilterByEnum.publishedAt,
        keyString: getFilterKeyString(key),
        operator: getFilterOperator(key, operator),
        value,
      }
    }) ?? []

  if (tagId) {
    postsFilterBy.push({
      key: PostListFilterByEnum.publishedAt,
      keyString: 'tagIds',
      operator: PostListFilterByOperator.contains,
      value: JSON.stringify(tagId),
    })
  }

  const mergedStaticFields = convertToStatic(
    fields,
    staticFields,
    customFields ?? [],
  )

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    usePosts({
      variables: {
        limit,
        spaceIds: [spaceId],
        orderByString: orderBy,
        reverse: reverseSort ?? true,
        filterBy: postsFilterBy,
        postTypeIds: [postTypeId],
      },
      fields: generateGqlFields({ staticFields: mergedStaticFields, style }),
      useInfiniteQueryOptions: {
        enabled: canGetPosts && !!postTypeId,
        refetchOnMount: 'always',
      },
    })

  const showMoreButton = useShowMoreButton({
    style,
    gallerySize,
    showMore,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  })

  if (!canGetPosts && space?.authMemberProps?.permissions) {
    return (
      <Card>
        <Card.Content>
          <div className="my-16">
            <EmptyState
              title={
                <T
                  defaultMessage="This {SPACE} is private"
                  id="Alerts.PrivateSpace"
                />
              }
              icon={<LockClosedIcon />}
              description={
                <T
                  defaultMessage="You need to be invited to this {SPACE} to access its posts"
                  id="Alerts.PrivateSpace.Description"
                />
              }
            />
          </div>
        </Card.Content>
      </Card>
    )
  }

  const { nodes: posts } = simplifyPaginatedResult(data) as { nodes: Post[] }

  if (!isLoading && data && posts && posts.length === 0) {
    return <PostsEmptyState />
  }

  if (isLoading || !space || !data || !space?.authMemberProps?.permissions) {
    return (
      <LoadingState
        count={limit}
        style={style}
        gallerySize={gallerySize}
        showDivider={showDivider}
      />
    )
  }

  return (
    <GenericPostsList
      style={style}
      posts={posts}
      fields={mergedStaticFields}
      gallerySize={gallerySize}
      showDivider={showDivider}
      activeFieldId={activeFieldId}
      showMoreButton={showMoreButton}
      linkTo={linkTo}
      preview={preview}
    />
  )
}
